import {
  NUMBER_NOT_KNOWN_ID,
  type HistorySync,
  type MessageAuthor,
  type ThreadSummary,
  type WhatsAppHandler,
  type WhatsAppMessage,
} from "@/api/whatsapp";
import type { StatusTone } from "@/components/shared/status-badge";
import {
  formatCount,
  formatDate,
  formatDateTime,
  formatHoursMinutes,
  formatTime,
  istDateString,
} from "@/lib/format";

/** Who a thread is, in the order people recognise: our record, their WhatsApp name, the number. */
export function threadTitle(thread: Pick<ThreadSummary, "people" | "contact">): string {
  return (
    thread.people[0]?.name ||
    thread.contact.name ||
    thread.contact.maskedPhone ||
    "Unknown contact"
  );
}

/** "RK" — two letters for the avatar; a phone icon stands in when there is no name. */
export function initialsOf(name: string): string | null {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter((word) => /\p{L}/u.test(word));
  if (!words.length) return null;
  const first = words[0]!.charAt(0);
  const last = words.length > 1 ? words[words.length - 1]!.charAt(0) : "";
  return (first + last).toUpperCase();
}

export const PERSON_LABEL = { customer: "Customer", champ: "Champ" } as const;

/** Time in a list row: the time today, the date before that. */
export function listTime(epochMs: number | null, now = Date.now()): string {
  if (!epochMs) return "";
  return istDateString(epochMs) === istDateString(now)
    ? formatTime(epochMs)
    : formatDate(epochMs);
}

export interface WindowState {
  open: boolean;
  tone: StatusTone;
  /** "Reply window open · 3h 12m left" */
  label: string;
}

/**
 * WhatsApp's 24-hour customer-service window: free text only while it is
 * open; after it, a template. Computed by the server from the contact's last
 * message; shown here with what is left of it.
 */
export function replyWindow(endsAt: number | null, now = Date.now()): WindowState {
  if (endsAt === null) {
    return {
      open: false,
      tone: "neutral",
      label: "They haven’t written · template only",
    };
  }
  if (endsAt <= now) {
    return { open: false, tone: "neutral", label: "Reply window closed · template only" };
  }
  const left = formatHoursMinutes((endsAt - now) / 1000);
  return {
    open: true,
    tone: endsAt - now < 2 * 3600_000 ? "warning" : "success",
    label: `Reply window open · ${left} left`,
  };
}

export function handlerLabel(handledBy: WhatsAppHandler, name: string): string {
  switch (handledBy) {
    case "ai":
      return name ? `AI answering · ${name}` : "AI answering";
    case "human":
      return name ? `${name} answering` : "A person is answering";
    default:
      return "Nobody assigned";
  }
}

export function handlerTone(handledBy: WhatsAppHandler): StatusTone {
  return handledBy === "ai" ? "info" : handledBy === "human" ? "success" : "neutral";
}

/** The line above a run of our side's messages. */
export function authorLabel(author: MessageAuthor, name: string): string {
  switch (author) {
    case "ai":
      return name ? `AI · ${name}` : "AI";
    case "staff":
      return name || "Team member";
    case "panel":
      return name ? `${name} · Panel` : "Admin panel";
    case "automation":
      return name || "Automation";
    case "system":
      return "WhatsApp";
    default:
      return name;
  }
}

/** "sales-agent/customer-status" → "Customer status", with "Sales agent" as its group. */
export function toolTitle(name: string | null): { title: string; group: string | null } {
  if (!name) return { title: "Tool result", group: null };
  const parts = name.split("/").filter(Boolean);
  const last = parts[parts.length - 1] ?? name;
  const words = (s: string) =>
    s
      .replace(/[-_]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .trim()
      .toLowerCase();
  const title = words(last);
  const group = parts.length > 1 ? words(parts[parts.length - 2]!) : null;
  return {
    title: title.charAt(0).toUpperCase() + title.slice(1),
    group: group ? group.charAt(0).toUpperCase() + group.slice(1) : null,
  };
}

/**
 * One line saying what a tool answered — its message when it has one, else
 * whether it succeeded, else the start of the raw answer.
 */
export function toolSummary(output: unknown): string {
  if (output === null || output === undefined) return "No answer";
  if (typeof output === "string") return oneLine(output);
  if (typeof output === "object" && !Array.isArray(output)) {
    const record = output as Record<string, unknown>;
    for (const key of ["message", "msg", "status", "error"]) {
      const value = record[key];
      if (typeof value === "string" && value.trim()) return oneLine(value);
    }
    if (record["success"] === true) return "Succeeded";
    if (record["success"] === false) return "Failed";
  }
  return oneLine(JSON.stringify(output));
}

function oneLine(text: string): string {
  const flat = maskPhones(text).replace(/\s+/g, " ").trim();
  return flat.length > 140 ? `${flat.slice(0, 139)}…` : flat;
}

/** An Indian mobile, bare or with 91 / +91 in front. */
const MOBILE = /(?<![\d])(?:\+?91[\s-]?)?[6-9]\d{9}(?![\d])/g;

/**
 * "919876543210" → "******3210". Numbers are shown masked everywhere in the
 * inbox (product owner, 2026-10-23), and a tool's input or answer usually
 * carries the contact's own number.
 */
export function maskPhones(text: string): string {
  return text.replace(MOBILE, (match) => `******${match.slice(-4)}`);
}

/**
 * Pretty JSON for the expanded tool card; strings that hold JSON are
 * unwrapped; phone numbers masked.
 */
export function prettyJson(value: unknown): string {
  if (typeof value === "string") {
    try {
      return maskPhones(JSON.stringify(JSON.parse(value), null, 2));
    } catch {
      return maskPhones(value);
    }
  }
  return maskPhones(JSON.stringify(value, null, 2) ?? "");
}

/** Whether two messages sit in the same visual run (same side, same author, close in time). */
export function sameRun(a: WhatsAppMessage | undefined, b: WhatsAppMessage): boolean {
  if (!a) return false;
  return (
    a.direction === b.direction &&
    a.author === b.author &&
    a.authorName === b.authorName &&
    a.kind !== "tool" &&
    b.kind !== "tool" &&
    b.sentAt - a.sentAt < 10 * 60_000
  );
}

/**
 * Text the AI wrote that never went out on WhatsApp (Chatzy keeps such turns:
 * `direction: "none"`, a plain kind, no status). Shown on our side, muted.
 */
export function isUnsentText(message: WhatsAppMessage): boolean {
  return (
    message.direction === "none" && message.kind !== "tool" && message.kind !== "event"
  );
}

/** The day the inbox went live; nothing older is stored until someone syncs history. */
export const INBOX_LIVE_DATE = "24/10/2026";

/** Shown wherever the "number not known" bucket is named. */
export const NUMBER_NOT_KNOWN_HINT =
  "Older Chatzy chats whose number Chatzy doesn’t tell us. They move to the right number when it becomes known.";

export function isNumberNotKnown(number: { id: number }): boolean {
  return number.id === NUMBER_NOT_KNOWN_ID;
}

export function isSyncActive(sync: HistorySync | null | undefined): boolean {
  return sync?.status === "queued" || sync?.status === "running";
}

/** The quiet line beside "Sync history". */
export function syncLine(sync: HistorySync | null | undefined): string {
  if (!sync) return "History not synced yet";
  if (isSyncActive(sync)) return "Syncing history… can take up to a minute";
  const at = sync.finishedAt ?? sync.startedAt ?? sync.createdAt;
  return sync.status === "failed"
    ? `Last sync failed · ${formatDateTime(at)}`
    : `Last synced ${formatDateTime(at)}`;
}

/**
 * The same, short enough for a phone's header — "Synced 24/10, 1:02 PM" —
 * so the time is never the part cut off. The year is dropped (a sync is
 * recent or it is "not synced"), never the time.
 */
export function syncLineShort(sync: HistorySync | null | undefined): string {
  if (!sync) return "Not synced yet";
  if (isSyncActive(sync)) return "Syncing history…";
  const at = sync.finishedAt ?? sync.startedAt ?? sync.createdAt;
  const when = `${formatDate(at).slice(0, 5)}, ${formatTime(at)}`;
  return sync.status === "failed" ? `Sync failed ${when}` : `Synced ${when}`;
}

/**
 * A finished sync in plain words, for its toast: "History synced — 312
 * messages added", and "Chatzy 280, WATI 32. 4 older Chatzy chats kept under
 * “number not known”." Problems are a short note, never the raw errors.
 */
export function syncSummary(sync: HistorySync): { title: string; description: string } {
  const result = sync.result;
  if (!result) return { title: "History synced", description: "" };
  const chatzy = result.chatzy.messagesAdded;
  const wati = result.wati.fromTicketReplies + result.wati.fromApi;
  const total = chatzy + wati;
  const notes: string[] = [];
  if (total > 0) notes.push(`Chatzy ${formatCount(chatzy)}, WATI ${formatCount(wati)}.`);
  if (result.chatzy.numberNotKnown > 0) {
    const n = result.chatzy.numberNotKnown;
    notes.push(
      `${formatCount(n)} older Chatzy ${n === 1 ? "chat" : "chats"} kept under “number not known”.`,
    );
  }
  if (result.wati.truncated) {
    notes.push("WATI had more history than one sync reads; only the newest came in.");
  }
  if (result.errors.length) notes.push("Some of it couldn’t be read — try again later.");
  return {
    title:
      total > 0
        ? `History synced — ${formatCount(total)} ${total === 1 ? "message" : "messages"} added`
        : "History synced — nothing new",
    description: notes.join(" "),
  };
}

/** The message's own words as a first guess at a title: one line, about 80 characters. */
export function titleFrom(message: WhatsAppMessage): string {
  const text = (message.body ?? message.reply?.title ?? "").replace(/\s+/g, " ").trim();
  if (!text) return "WhatsApp message";
  if (text.length <= 80) return text;
  const cut = text.slice(0, 80);
  const space = cut.lastIndexOf(" ");
  return `${(space > 40 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/** Whether a customer's message may have a ticket made from it. */
export function canTicket(message: WhatsAppMessage): boolean {
  return (
    message.id > 0 &&
    message.direction === "in" &&
    message.kind !== "event" &&
    message.kind !== "tool"
  );
}
